import React, { useState } from 'react';
import { Wallet, Zap, ArrowRight, Check, AlertCircle, Coins, Gift } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

interface Package {
  id: string;
  mon: number;
  credits: number;
  bonus?: number;
  popular?: boolean;
}

const PACKAGES: Package[] = [
  { id: 'starter', mon: 50, credits: 500 },
  { id: 'standard', mon: 100, credits: 1000, bonus: 100, popular: true },
  { id: 'premium', mon: 500, credits: 5000, bonus: 750 },
  { id: 'mega', mon: 1000, credits: 10000, bonus: 2000 },
];

const TokenRecharge = () => {
  const { user } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [step, setStep] = useState<'select' | 'confirm' | 'success'>('select');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSelectPackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    setCustomAmount('');
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedPackage(null);
  };

  const getTotalCredits = () => {
    if (selectedPackage) {
      return selectedPackage.credits + (selectedPackage.bonus || 0);
    }
    const mon = parseInt(customAmount) || 0;
    return mon * 10;
  };

  const getMonAmount = () => {
    if (selectedPackage) return selectedPackage.mon;
    return parseInt(customAmount) || 0;
  };

  const handleConfirm = () => {
    setIsProcessing(true);
    // Simulate blockchain transaction
    setTimeout(() => {
      setIsProcessing(false);
      setStep('success');
    }, 2000);
  };

  const reset = () => {
    setStep('select');
    setSelectedPackage(null);
    setCustomAmount('');
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-surface border border-line flex items-center justify-center">
            <Wallet size={32} className="text-muted" />
          </div>
          <p className="text-muted">Please connect your wallet to recharge credits</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <section className="px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-[500px] mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center">
            <Check size={40} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Recharge Successful!</h2>
          <p className="text-muted mb-6">
            You have successfully purchased {getTotalCredits().toLocaleString()} credits
          </p>
          <div className="p-4 bg-surface border border-line rounded-xl mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-muted">Amount Paid</span>
              <span className="font-mono font-bold">{getMonAmount()} $MON</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">Credits Received</span>
              <span className="font-mono font-bold text-gold">{getTotalCredits().toLocaleString()}</span>
            </div>
          </div>
          <button
            onClick={reset}
            className="px-8 py-3 bg-gold text-bg rounded-lg font-semibold hover:bg-white transition-colors"
          >
            Recharge More
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 md:px-8 py-16 md:py-24">
      <div className="max-w-[800px] mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-ink mb-4">
            Recharge Credits
          </h1>
          <p className="text-muted">
            Purchase AI credits to power your agents in battle
          </p>
        </div>

        {/* Current Balance */}
        <div className="mb-8 p-6 border border-gold/30 rounded-xl bg-gold/5 text-center">
          <div className="text-sm text-muted mb-2">Current Balance</div>
          <div className="text-4xl font-mono font-bold text-gold mb-1">
            1,500
          </div>
          <div className="text-sm text-gold/70">Credits</div>
        </div>

        {step === 'select' ? (
          <>
            {/* Rate Info */}
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-surface border border-line rounded-full">
                <Coins size={16} className="text-gold" />
                <span className="text-sm">1 $MON = 10 Credits</span>
              </div>
            </div>

            {/* Packages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {PACKAGES.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handleSelectPackage(pkg)}
                  className={cn(
                    "relative p-5 border rounded-xl text-center transition-all",
                    selectedPackage?.id === pkg.id
                      ? "border-gold bg-gold/5"
                      : "border-line hover:border-gold/30"
                  )}
                >
                  {pkg.popular && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gold text-bg text-[10px] font-bold rounded-full">
                      POPULAR
                    </div>
                  )}
                  <div className="text-sm text-muted mb-1">{pkg.mon} $MON</div>
                  <div className="text-2xl font-mono font-bold text-gold mb-1">
                    {pkg.credits.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted">Credits</div>
                  {pkg.bonus && (
                    <div className="mt-2 text-xs text-green-500">
                      +{pkg.bonus} Bonus
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="mb-8">
              <div className="text-sm font-semibold mb-3 text-center">Or Enter Custom Amount</div>
              <div className="flex items-center gap-4 max-w-md mx-auto">
                <div className="flex-1 relative">
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => handleCustomAmount(e.target.value)}
                    placeholder="Enter $MON amount"
                    className="w-full px-4 py-3 bg-bg border border-line rounded-lg focus:outline-none focus:border-gold/50"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted text-sm">$MON</span>
                </div>
                <ArrowRight size={20} className="text-muted" />
                <div className="flex-1 p-3 bg-surface border border-line rounded-lg text-center">
                  <span className="font-mono font-bold text-gold">
                    {customAmount ? (parseInt(customAmount) * 10).toLocaleString() : '0'}
                  </span>
                  <span className="text-xs text-muted ml-1">Credits</span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="p-4 bg-surface border border-line rounded-xl mb-8">
              <div className="flex items-start gap-3">
                <AlertCircle size={18} className="text-gold mt-0.5" />
                <div className="text-sm text-muted">
                  <p className="mb-1"><strong>What are credits?</strong></p>
                  <p>Credits are consumed when your AI agent makes decisions during battles. Each action (move, attack, defend) costs credits. When credits run out, your agent stops responding.</p>
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <button
              onClick={() => setStep('confirm')}
              disabled={!selectedPackage && !customAmount}
              className={cn(
                "w-full py-4 rounded-xl font-semibold text-lg transition-colors",
                selectedPackage || customAmount
                  ? "bg-gold text-bg hover:bg-white"
                  : "bg-muted text-muted cursor-not-allowed"
              )}
            >
              Continue
            </button>
          </>
        ) : (
          /* Confirm Step */
          <div className="max-w-md mx-auto">
            <div className="p-6 border border-line rounded-xl mb-6">
              <h3 className="text-lg font-semibold mb-4">Confirm Purchase</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Package</span>
                  <span className="font-medium">
                    {selectedPackage 
                      ? `${selectedPackage.mon} $MON Package` 
                      : `${customAmount} $MON Custom`}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Credits</span>
                  <span className="font-mono font-bold text-gold">
                    {getTotalCredits().toLocaleString()}
                  </span>
                </div>
                {selectedPackage?.bonus && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Bonus</span>
                    <span className="text-green-500">+{selectedPackage.bonus}</span>
                  </div>
                )}
                <div className="border-t border-line pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="font-mono font-bold">{getMonAmount()} $MON</span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-surface border border-line rounded-lg mb-6">
                <div className="flex items-center gap-2 text-sm text-muted">
                  <Wallet size={16} />
                  <span>Paying from: {user.walletAddress.slice(0, 6)}...{user.walletAddress.slice(-4)}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select')}
                  className="flex-1 py-3 border border-line rounded-lg text-muted hover:text-ink transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isProcessing}
                  className={cn(
                    "flex-1 py-3 rounded-lg font-semibold transition-colors",
                    isProcessing
                      ? "bg-muted text-muted cursor-not-allowed"
                      : "bg-gold text-bg hover:bg-white"
                  )}
                >
                  {isProcessing ? 'Processing...' : 'Confirm Purchase'}
                </button>
              </div>
            </div>

            <p className="text-xs text-muted text-center">
              By confirming, you authorize the transfer of {getMonAmount()} $MON from your wallet.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default TokenRecharge;
